/**
 * Quality checks for a traced scan.
 *
 * Every flag is an informational note: it is recorded with the parts, rendered
 * as a grey sentence, and refuses NOTHING — not a save, not an approval. The
 * first live factory test settled that direction: a stored flag cannot
 * self-clear (re-trace is short-circuited once a verdict is stored), so any
 * flag that gated the workflow was a permanent block on a sheet nobody could
 * fix at the scanner. Every flag carries the index of the shape it belongs to;
 * the UI owns all user-facing wording. `detail` is English diagnostic text for
 * logs and developers, never something to render at a user.
 */

import { erodeMask, labelComponents } from './components'
import { ringArea } from './geometry'
import type { Point } from './geometry'
import type { GrayRaster } from './raster'
import type { TracedShape } from './shapes'

export interface QualityFlag {
  code: 'touching_suspect' | 'too_small' | 'invalid_ring'
  shapeIndex: number
  detail: string
}

/**
 * Erosion radius of the neck test, in pixels: a 3 px radius is a 7 × 7 square,
 * so any bridge 6 px wide or thinner is dissolved. At the 300 dpi the scanner
 * profile targets that is a hair over half a millimetre — narrower than
 * anything a knife leaves between two genuinely separate pieces, and far
 * narrower than the thinnest real waist on a saya part.
 */
export const NECK_EROSION_RADIUS_PX = 3

/**
 * Rasterises a shape's outer ring into a mask over the given window, by
 * even-odd scanline fill sampled at pixel centres.
 *
 * The neck test runs on the *polygon*, not on a re-thresholded raster, for two
 * reasons: `qualityCheck` is not given the threshold that produced the shapes,
 * and the polygon is the thing that gets stored — if simplification closed a
 * neck, the stored geometry has no neck and there is nothing to flag.
 *
 * Sampling at pixel centres means the filled mask can differ from the source
 * component by a pixel along the bottom/right edges (the standard half-open
 * scanline rule). That is immaterial to a test that then erodes 3 px.
 */
function rasterizeShape(shape: TracedShape, x0: number, y0: number, width: number, height: number): Uint8Array {
  const mask = new Uint8Array(width * height)
  const rings: readonly Point[][] = [shape.outer]
  const crossings: number[] = []

  for (let row = 0; row < height; row += 1) {
    const y = y0 + row
    crossings.length = 0

    for (const ring of rings) {
      const n = ring.length
      if (n < 3) continue
      for (let i = 0; i < n; i += 1) {
        const a = ring[i]!
        const b = ring[(i + 1) % n]!
        // Half-open in y: an edge counts when the scanline separates its ends.
        // Comparing "<=" on both sides makes a vertex shared by two edges count
        // once, which is what keeps the parity right at every corner.
        if ((a[1] <= y) === (b[1] <= y)) continue
        crossings.push(a[0] + ((y - a[1]) * (b[0] - a[0])) / (b[1] - a[1]))
      }
    }

    if (crossings.length < 2) continue
    crossings.sort((p, q) => p - q)

    const rowOffset = row * width
    for (let i = 0; i + 1 < crossings.length; i += 2) {
      const from = Math.max(x0, Math.ceil(crossings[i]!))
      const to = Math.min(x0 + width - 1, Math.floor(crossings[i + 1]!))
      for (let x = from; x <= to; x += 1) mask[rowOffset + (x - x0)] = 1
    }
  }

  return mask
}

/**
 * Neck heuristic: does a 3 px erosion break this shape into two or more pieces
 * that are each big enough to be a part?
 *
 * Two saya pieces scanned touching produce one component joined by a short,
 * thin bridge. Erosion eats a fixed band off every boundary, so the bridge
 * vanishes long before either piece does, and the piece count jumps from one to
 * two. Filtering the eroded pieces by `minAreaPx` is what keeps the test from
 * firing on a shape that merely sheds a whisker.
 */
function splitsUnderErosion(
  shape: TracedShape,
  raster: GrayRaster,
  minAreaPx: number,
): number {
  const x0 = Math.max(0, Math.min(shape.bboxPx[0], raster.width - 1))
  const y0 = Math.max(0, Math.min(shape.bboxPx[1], raster.height - 1))
  const x1 = Math.max(x0, Math.min(shape.bboxPx[2], raster.width - 1))
  const y1 = Math.max(y0, Math.min(shape.bboxPx[3], raster.height - 1))
  const width = x1 - x0 + 1
  const height = y1 - y0 + 1

  const filled = rasterizeShape(shape, x0, y0, width, height)
  const eroded = erodeMask(filled, width, height, NECK_EROSION_RADIUS_PX)
  const pieces = labelComponents(eroded, width, height, 8)

  let survivors = 0
  for (let id = 1; id <= pieces.count; id += 1) {
    if (pieces.sizes[id]! >= minAreaPx) survivors += 1
  }
  return survivors
}

/**
 * Runs every check over a traced scan.
 *
 * Flags are grouped by shape in `shapes` order, and within a shape they run
 * from most to least fundamental: `invalid_ring` (the geometry is unusable),
 * `too_small` (it is usable but below the trusted feature size), then
 * `touching_suspect` (the geometry is fine, the *scan* is not). A shape whose
 * outer ring is invalid is not neck-tested — there is nothing coherent to
 * rasterise.
 *
 * `minAreaPx` should be the same value that was passed to `traceShapes`:
 * `traceShapes` drops components below it by *pixel count*, while `too_small`
 * measures the *polygon* area, so a long ragged sliver that survived the pixel
 * gate can still be caught here.
 */
export function qualityCheck(shapes: TracedShape[], r: GrayRaster, minAreaPx: number): QualityFlag[] {
  const flags: QualityFlag[] = []

  shapes.forEach((shape, shapeIndex) => {
    const outerArea = ringArea(shape.outer)
    const outerValid = shape.outer.length >= 3 && outerArea > 0

    if (!outerValid) {
      flags.push({
        code: 'invalid_ring',
        shapeIndex,
        detail:
          shape.outer.length < 3
            ? `outer ring has ${shape.outer.length} point(s), needs at least 3`
            : 'outer ring encloses zero area',
      })
    }

    if (shape.areaPx < minAreaPx) {
      flags.push({
        code: 'too_small',
        shapeIndex,
        detail: `net area ${shape.areaPx.toFixed(1)} px² is below the ${minAreaPx} px² minimum`,
      })
    }

    if (outerValid) {
      const pieces = splitsUnderErosion(shape, r, minAreaPx)
      if (pieces >= 2) {
        flags.push({
          code: 'touching_suspect',
          shapeIndex,
          detail: `a ${NECK_EROSION_RADIUS_PX} px erosion splits this outline into ${pieces} pieces — it is probably two parts scanned touching`,
        })
      }
    }
  })

  return flags
}
